import React, { FC, useEffect, useState } from 'react';
import { Select } from 'chakra-react-select';
import { FormControl, FormLabel, Text } from '@chakra-ui/react';
import { useField, useFormikContext } from 'formik';
import { EmployeeFilterableSelectProps } from './EmployeeFilterableSelect.props';
import { useQuery } from 'react-query';
import { washHub } from 'store';
import { fetchEmployees } from 'api/employees';
import { useWashHubListener } from 'hooks';

type OptionType = { label: string; value: string };

const EmployeeFilterableSelectView: FC<EmployeeFilterableSelectProps> = ({
  isReadOnly,
  containerProps = {},
  label,
  name,
  labelProps,
  isRequired = true,
  selectedInventoryItem,
  isMulti,
  getAllEmployees,
  onChange,
}) => {
  const [{ value }, meta] = useField<string | string[] | undefined>(name);
  const [options, setOptions] = useState<OptionType[]>([]);
  const { setFieldValue } = useFormikContext<any>();
  const washHubId = washHub.selectors.useSelectedWashHubId();
  const [page, setPage] = useState<number>(1);

  const queryKey = [
    `fetch-employees-${washHubId}`,
    washHubId,
    page,
    getAllEmployees,
  ];

  const queryFn = () =>
    fetchEmployees({
      page: page,
      washHub: washHubId,
      limit: 1000,
      ...(getAllEmployees ? { getAll: true } : {}),
    } as any);

  const _options = {
    keepPreviousData: true,
  };
  useWashHubListener(() => {
    setPage(1);
  });
  const { data, isLoading } = useQuery(queryKey, queryFn, _options);

  useEffect(() => {
    if (data) {
      const valueData = data.items.map((p: any) => ({
        label: p.fname + ' ' + p.lname,
        value: p._id,
      }));
      setOptions(valueData || []);
    }
  }, [data]);

  let _values: OptionType | OptionType[] | undefined = undefined;
  if (value && data?.items) {
    if (Array.isArray(value)) {
      _values = data.items
        .filter((p: any) => value?.includes(p._id) || value?.includes(p.name))
        .map((p: any) => ({ label: p.fname + ' ' + p.lname, value: p._id }));
    } else if (typeof value === 'string') {
      const found = data.items.find(
        (p: any) => p._id === value || p.name === value,
      );
      _values = found
        ? { label: found.fname + ' ' + found.lname, value: found._id }
        : undefined;
    }
  }

  useEffect(() => {
    if (selectedInventoryItem) {
      const selectedInventoryItemId: string = selectedInventoryItem?.site;
      setFieldValue(name, selectedInventoryItemId);
    }
  }, []);

  return (
    <FormControl {...containerProps} isRequired={isRequired}>
      <FormLabel
        htmlFor={name}
        textTransform="capitalize"
        fontSize="14px"
        color="#afafaf"
        {...labelProps}>
        {label}
      </FormLabel>
      <Select
        name={name}
        placeholder="Select"
        value={_values}
        isDisabled={isLoading || isReadOnly}
        isLoading={isLoading}
        options={options}
        isMulti={!!isMulti}
        tagVariant="solid"
        colorScheme="primary"
        closeMenuOnSelect={!isMulti}
        chakraStyles={{
          dropdownIndicator: (_props: any) => ({
            ..._props,
            bg: 'transparent',
          }),
          option: (_props: any) => ({
            ..._props,
            padding: '10px',
            borderBottom: '1px solid #ccc',
          }),
          menuList: (_props: any) => ({
            ..._props,
            padding: '0px',
            marginBottom: '20px',
          }),
        }}
        onChange={(newValue: any) => {
          if (isMulti) {
            const valuesArray = Array.isArray(newValue)
              ? (newValue as OptionType[]).map((opt) => opt.value)
              : [];
            setFieldValue(name, valuesArray);
            onChange?.(valuesArray);
          } else {
            const site = (newValue as OptionType | null | undefined)?.value;
            setFieldValue(name, site ?? '');
            onChange?.(site ?? '');
          }
        }}
      />
      {meta.error && meta.touched && (
        <Text color="red.500" fontSize="13px">
          {meta.error}
        </Text>
      )}
    </FormControl>
  );
};

export default EmployeeFilterableSelectView;
